import { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap, useGSAP } from '@/lib/gsap-config';
import { IdeaFlowDemo } from './IdeaFlowDemo';
import { cn } from '@/lib/utils';

interface PromptPreset {
  id: string;
  label: string;
  role: string;
  badge: string;
  prompt: string;
  slides: {
    title: string;
    points: string[];
    tag: string;
  }[];
}

const presetsVi: PromptPreset[] = [
  {
    id: 'teacher',
    label: 'Giáo viên',
    role: 'Sư phạm Lịch sử 12',
    badge: 'CHUẨN GDPT 2018',
    prompt: 'Soạn giáo án Lịch sử 12: Chiến dịch Điện Biên Phủ 1954 gồm ý nghĩa lịch sử, diễn biến 3 giai đoạn tiến công và câu hỏi trắc nghiệm tương tác cho học sinh.',
    slides: [
      {
        title: 'Bối cảnh Lịch sử & Kế hoạch Nava của Pháp',
        points: ['Âm mưu xây dựng tập đoàn cứ điểm Điện Biên Phủ bất khả xâm phạm', 'Chủ trương chiến lược của Trung ương Đảng và Đại tướng Võ Nguyên Giáp'],
        tag: 'SLIDE 1 // BỐI CẢNH',
      },
      {
        title: 'Diễn biến 3 Đợt Tấn công Lịch sử (13/3 - 7/5/1954)',
        points: ['Đợt 1: Tiêu diệt Him Lam, Độc Lập', 'Đợt 2: Chiến sự đồi A1, C1', 'Đợt 3: Tổng công kích, bắt sống tướng De Castries'],
        tag: 'SLIDE 2 // DIỄN BIẾN',
      },
      {
        title: 'Ý nghĩa Lịch sử & Câu hỏi Tương tác Phản xạ',
        points: ['Chấm dứt hoàn toàn ách thống trị của thực dân Pháp', 'Trắc nghiệm: Trận đánh mở màn diễn ra tại cứ điểm nào?'],
        tag: 'SLIDE 3 // TRẮC NGHIỆM',
      },
    ],
  },
  {
    id: 'student',
    label: 'Sinh viên',
    role: 'Bảo vệ Khóa luận Tốt nghiệp',
    badge: 'HỌC THUẬT APA 7TH',
    prompt: 'Tạo slide bảo vệ KLTN: Ứng dụng mô hình Deep Learning Transformer phân loại văn bản tiếng Việt chuẩn định dạng APA 7th và công thức toán.',
    slides: [
      {
        title: 'Tính Cấp thiết & Mục tiêu Nghiên cứu',
        points: ['Thách thức xử lý ngôn ngữ tự nhiên với ngữ cảnh tiếng Việt', 'Mục tiêu: Đạt F1-score > 92% trên bộ dữ liệu UIT-VSFC'],
        tag: 'SLIDE 1 // TỔNG QUAN',
      },
      {
        title: 'Kiến trúc Mô hình PhoBERT kết hợp Bi-LSTM',
        points: ['Cơ chế Self-Attention đa tầng với 12 layers', 'Tối ưu hàm mất mát Cross-Entropy và thuật toán AdamW'],
        tag: 'SLIDE 2 // KIẾN TRÚC',
      },
      {
        title: 'Đánh giá Thực nghiệm & Đóng góp Đề tài',
        points: ['So sánh độ chính xác với các baseline truyền thống', 'Đóng góp công cụ trích xuất thực thể cho cộng đồng nghiên cứu'],
        tag: 'SLIDE 3 // KẾT QUẢ',
      },
    ],
  },
  {
    id: 'office',
    label: 'Văn phòng',
    role: 'Kế hoạch Q4 Doanh nghiệp',
    badge: 'CHUẨN GỌI VỐN / BÁO CÁO',
    prompt: 'Tạo slide báo cáo tài chính và kế hoạch kinh doanh Q4: Tăng trưởng doanh thu 182%, tối ưu chi phí vận hành 34% và lộ trình mở rộng thị trường.',
    slides: [
      {
        title: 'Bức tranh Toàn cảnh Q3: Tăng trưởng Vượt kế hoạch',
        points: ['Tổng doanh thu đạt 4.8 triệu USD, tăng 182% YoY', 'Biên lợi nhuận gộp cải thiện lên mức 42% nhờ tự động hóa'],
        tag: 'SLIDE 1 // TÀI CHÍNH',
      },
      {
        title: 'Tối ưu Chi phí Vận hành & Năng suất Chuỗi cung ứng',
        points: ['Cắt giảm 34% chi phí logistics qua thuật toán điều phối', 'Thời gian hoàn tất đơn hàng rút ngắn từ 48h xuống 14h'],
        tag: 'SLIDE 2 // VẬN HÀNH',
      },
      {
        title: 'Chiến lược Mở rộng Q4 & Dự báo Dòng tiền 2025',
        points: ['Thâm nhập 3 thị trường khu vực Đông Nam Á', 'Kế hoạch phân bổ ngân sách R&D và tuyển dụng nhân sự cấp cao'],
        tag: 'SLIDE 3 // CHIẾN LƯỢC',
      },
    ],
  },
];

const presetsEn: PromptPreset[] = [
  {
    id: 'teacher',
    label: 'Educator',
    role: 'Grade 12 History Syllabus',
    badge: 'CURRICULUM ACCREDITED',
    prompt: 'Prepare Grade 12 History lesson: Battle of Dien Bien Phu 1954 including historical significance, 3 tactical assault phases, and interactive student quiz.',
    slides: [
      {
        title: 'Historical Background & French Navarre Plan',
        points: ['Strategy of fortified stronghold at Dien Bien Phu', 'Strategic counter-measures by Military Command and General Vo Nguyen Giap'],
        tag: 'SLIDE 1 // CONTEXT',
      },
      {
        title: '3 Historic Assault Phases (March 13 - May 7, 1954)',
        points: ['Phase 1: Him Lam & Doc Lap neutralized', 'Phase 2: Heavy engagements at Hill A1 & C1', 'Phase 3: General offensive & capture of General De Castries'],
        tag: 'SLIDE 2 // PHASES',
      },
      {
        title: 'Historical Significance & Interactive Quiz',
        points: ['Complete termination of colonial rule in Indochina', 'Quiz: Where was the opening battle fought?'],
        tag: 'SLIDE 3 // QUIZ',
      },
    ],
  },
  {
    id: 'student',
    label: 'Student',
    role: 'Graduate Thesis Defense',
    badge: 'ACADEMIC APA 7TH',
    prompt: 'Generate thesis defense deck: Transformer Deep Learning Models for Vietnamese Text Classification formatted to APA 7th with formulas.',
    slides: [
      {
        title: 'Motivation & Research Objectives',
        points: ['Natural Language Processing challenges in Vietnamese context', 'Goal: Attain F1-score > 92% on UIT-VSFC dataset benchmark'],
        tag: 'SLIDE 1 // OVERVIEW',
      },
      {
        title: 'PhoBERT Combined with Bi-LSTM Architecture',
        points: ['Multi-layer Self-Attention mechanism across 12 encoder layers', 'Cross-Entropy loss optimization with AdamW scheduler'],
        tag: 'SLIDE 2 // ARCHITECTURE',
      },
      {
        title: 'Empirical Evaluation & Contributions',
        points: ['Comparative accuracy versus traditional baseline models', 'Open-source entity extraction pipeline for the research community'],
        tag: 'SLIDE 3 // RESULTS',
      },
    ],
  },
  {
    id: 'office',
    label: 'Enterprise',
    role: 'Q4 Executive Business Plan',
    badge: 'EXECUTIVE / PITCH DECK',
    prompt: 'Generate financial report & Q4 business roadmap: 182% YoY revenue growth, 34% operational cost reduction, and Southeast Asia expansion.',
    slides: [
      {
        title: 'Q3 Macro Overview: Outperforming Forecasts',
        points: ['Gross revenue reached $4.8M, representing 182% YoY growth', 'Gross margin expanded to 42% via automated pipelines'],
        tag: 'SLIDE 1 // FINANCIALS',
      },
      {
        title: 'Operational Cost Optimization & Supply Chain Velocity',
        points: ['Reduced logistics costs by 34% through algorithmic dispatching', 'Order fulfillment cycle trimmed from 48h to 14h'],
        tag: 'SLIDE 2 // OPERATIONS',
      },
      {
        title: 'Q4 Expansion Strategy & 2025 Cash Flow Forecast',
        points: ['Entering 3 high-growth Southeast Asian markets', 'Capital allocation for R&D and executive talent acquisition'],
        tag: 'SLIDE 3 // STRATEGY',
      },
    ],
  },
];

export function PromptLabSection() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const presets = useMemo(() => (isEn ? presetsEn : presetsVi), [isEn]);

  const [activePresetId, setActivePresetId] = useState<string>('teacher');
  const activePreset = presets.find((p) => p.id === activePresetId) || presets[0]!;

  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const handleSelectPreset = (presetId: string) => setActivePresetId(presetId);

  // Initial draw of circuit and text scrub on scroll
  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
    // Text scrub highlight on scroll
    const wordElements = textRef.current?.querySelectorAll('.scrub-word');
    if (wordElements && wordElements.length > 0) {
      // Force initial dimmed state
      gsap.set(wordElements, { opacity: 0.2 });

      gsap.fromTo(
        wordElements,
        { opacity: 0.2 },
        {
          opacity: 1,
          stagger: 0.1,
          ease: 'none',
          immediateRender: false,
          scrollTrigger: {
            trigger: textRef.current,
            start: 'top 75%',
            end: 'bottom 35%',
            scrub: 1,
            invalidateOnRefresh: true,
          },
        }
      );
    }
    });
    return () => media.revert();
  }, { scope: containerRef, dependencies: [isEn], revertOnUpdate: true });

  const philosophyPhrase = t('lab.philosophyText');
  const words = philosophyPhrase.split(' ');

  return (
    <section
      id="ideation-lab"
      tabIndex={-1}
      ref={containerRef}
      className="ideation-studio py-24 md:py-36 bg-background border-b border-border/80 relative overflow-hidden"
    >
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 mb-12 border-b border-border/60">
          <div>
            <div className="font-mono text-xs text-brand font-bold uppercase tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 bg-brand inline-block" />
              <span>[{t('lab.act')}]</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground mt-2">
              {t('lab.title')}
            </h2>
          </div>
          <p className="text-xs sm:text-sm font-mono text-muted-foreground max-w-xs text-left sm:text-right">
            {t('lab.subtitle')}
          </p>
        </div>

        {/* Audience Prompt Switcher Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {presets.map((p) => {
            const isSelected = p.id === activePreset.id;
            return (
              <button
                key={p.id}
                onClick={() => handleSelectPreset(p.id)}
                aria-pressed={isSelected}
                className={cn(
                  'px-4 py-2 rounded-btn font-mono text-xs font-semibold border transition-all cursor-pointer flex items-center gap-2',
                  isSelected
                    ? 'border-brand bg-brand/10 text-foreground shadow-subtle'
                    : 'border-border bg-card/60 text-muted-foreground hover:text-foreground hover:border-border/80'
                )}
              >
                <span>{p.label}</span>
                <span className="text-[10px] text-muted-foreground opacity-70">({p.role})</span>
              </button>
            );
          })}
        </div>

        <IdeaFlowDemo preset={activePreset} />

        {/* Narrative Flow: Text Scrub Philosophical Statement */}
        <div className="pt-24 mt-20 border-t border-border/60 text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 font-mono text-xs text-brand font-bold uppercase tracking-widest mb-6">
            <span className="w-2 h-2 bg-brand inline-block" />
            <span>{t('lab.philosophyTag')}</span>
          </div>

          <p
            ref={textRef}
            className="text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.3] text-foreground select-none"
          >
            {words.map((word, index) => (
              <span
                key={index}
                className="scrub-word inline-block mr-[0.28em] will-change-[opacity]"

              >
                {word}
              </span>
            ))}
          </p>
        </div>
      </div>
    </section>
  );
}

export default PromptLabSection;

